import type { ReactElement, SVGProps } from 'react'

// Stroke-only, 1.7px weight, rounded caps — see design.md §6.
type IcProps = SVGProps<SVGSVGElement> & { size?: number }

const Ic =
  (children: ReactElement) =>
  ({ size = 18, ...rest }: IcProps) =>
    (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        // Icons are decorative — never let the (stroke-only) glyph capture the
        // pointer. Without this the cursor flickers between arrow (over the
        // transparent gaps) and pointer (over the painted stroke) inside a
        // button. Set at the source so it holds regardless of the wrapper.
        pointerEvents="none"
        {...rest}
      >
        {children}
      </svg>
    )

export const IconCalendar = Ic(
  <>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M8 3v4M16 3v4M3 10h18" />
  </>
)

export const IconUsers = Ic(
  <>
    <circle cx="9" cy="8" r="3.5" />
    <path d="M2 20c0-3.5 3.13-6 7-6s7 2.5 7 6" />
    <circle cx="17" cy="10" r="2.5" />
    <path d="M22 20c0-2.5-2-4.5-5-4.5" />
  </>
)

export const IconSettings = Ic(
  <>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1 1.56V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.11-1.56 1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.7 1.7 0 0 0 .34-1.87 1.7 1.7 0 0 0-1.56-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.56-1.11 1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.7 1.7 0 0 0 1.87.34h.05a1.7 1.7 0 0 0 1-1.56V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.56h.05a1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.34 1.87v.05a1.7 1.7 0 0 0 1.56 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
  </>
)

export const IconLeaderboard = Ic(
  <path d="M4 21h4V10H4zM10 21h4V3h-4zM16 21h4v-7h-4z" />
)

export const IconFlag = Ic(<path d="M4 22V4M4 4h13l-2 4 2 4H4" />)

export const IconPokerChip = Ic(
  <>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="4" />
    <path d="M12 3v3M12 18v3M3 12h3M18 12h3" />
  </>
)

export const IconClubs = Ic(
  <>
    <circle cx="8" cy="9" r="3" />
    <circle cx="16" cy="9" r="3" />
    <circle cx="12" cy="15" r="3" />
    <path d="M12 18v3M10 21h4" />
  </>
)

export const IconChevDown = Ic(<path d="M6 9l6 6 6-6" />)
export const IconChevUp = Ic(<path d="M6 15l6-6 6 6" />)
export const IconChevRight = Ic(<path d="M9 6l6 6-6 6" />)
export const IconBack = Ic(<path d="M15 6l-6 6 6 6" />)
export const IconBell = Ic(
  <>
    <path d="M6 8a6 6 0 1 1 12 0c0 7 3 7 3 9H3c0-2 3-2 3-9" />
    <path d="M10 21a2 2 0 0 0 4 0" />
  </>
)
export const IconMore = Ic(
  <>
    <circle cx="5" cy="12" r="1" />
    <circle cx="12" cy="12" r="1" />
    <circle cx="19" cy="12" r="1" />
  </>
)
export const IconPlus = Ic(<path d="M12 5v14M5 12h14" />)
export const IconCheck = Ic(<path d="M5 12l5 5 9-11" />)
export const IconX = Ic(<path d="M6 6l12 12M18 6L6 18" />)
export const IconLogout = Ic(
  <>
    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
    <path d="M10 17l-5-5 5-5M5 12h12" />
  </>
)
export const IconHome = Ic(
  <>
    <path d="M3 11l9-8 9 8" />
    <path d="M5 10v10h14V10" />
  </>
)
export const IconCard = Ic(
  <>
    <rect x="3" y="6" width="18" height="13" rx="2" />
    <path d="M3 11h18M7 16h3" />
  </>
)
export const IconMessage = Ic(
  <>
    <path d="M21 12a8 8 0 0 1-11.5 7.2L4 21l1.8-5A8 8 0 1 1 21 12z" />
    <path d="M8 11h8M8 14h5" />
  </>
)
export const IconReport = Ic(
  <>
    <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
    <path d="M14 3v6h6M8 13h8M8 17h5" />
  </>
)
export const IconBuilding = Ic(
  <>
    <rect x="4" y="3" width="16" height="18" rx="1.5" />
    <path d="M9 8h.01M15 8h.01M9 12h.01M15 12h.01M9 16h.01M15 16h.01" />
    <path d="M10 21v-3h4v3" />
  </>
)
export const IconDownload = Ic(
  <>
    <path d="M12 4v12M6 12l6 6 6-6" />
    <path d="M4 20h16" />
  </>
)
export const IconRotateCcw = Ic(
  <>
    <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
    <path d="M3 3v5h5" />
  </>
)
export const IconExternal = Ic(
  <>
    <path d="M15 3h6v6" />
    <path d="M10 14L21 3" />
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
  </>
)

export const POLFICONS = {
  IconCalendar,
  IconUsers,
  IconSettings,
  IconLeaderboard,
  IconFlag,
  IconPokerChip,
  IconClubs,
  IconChevDown,
  IconChevUp,
  IconChevRight,
  IconBack,
  IconBell,
  IconMore,
  IconPlus,
  IconCheck,
  IconX,
  IconLogout,
  IconHome,
  IconCard,
  IconMessage,
  IconReport,
  IconBuilding,
  IconDownload,
  IconRotateCcw,
}
